import React, { ChangeEvent, FormEvent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { useProject } from "../contexts/ProjectContext";
import { getApiUrl } from "../utils/api";
import { authFetch } from "../utils/authFetch";
import "../App.css";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { t } from '@lingui/core/macro';

const FEEDBACK_TAGS = [
  "Bug – Functional",
  "Bug – Visual/UI",
  "Bug – Performance",
  "Bug – Compatibility",
  "Feature Request",
  "Enhancement",
  "UX Improvement",
  "UI Improvement",
  "QA",
  "Content Feedback",
  "Question / Clarification",
  "Other",
] as const;

const FEEDBACK_ATTACHMENT_EXTENSIONS = new Set([
  "html", "htm", "jpeg", "jpg", "m4v", "mov", "mp4", "pdf", "png", "tif", "tiff", "webm", "webp", "zip",
]);

const getTagTone = (tag: string): string => {
  if (tag.startsWith("Bug")) return "bug";
  if (tag === "Feature Request" || tag === "Enhancement") return "feature";
  if (tag === "UX Improvement" || tag === "UI Improvement") return "design";
  if (tag === "QA") return "qa";
  if (tag === "Content Feedback") return "content";
  if (tag === "Question / Clarification") return "question";
  return "other";
};

type ProjectAttachment = "google_sheets" | "excel";

type FeedbackResult = {
  kind: "success" | "error";
  message: string;
};

type ErrorResponse = {
  message?: string;
  warnings?: string[];
};

export default function Feedback(): React.ReactElement {
  const navigate = useNavigate();
  const { selectedProject } = useProject();

  const [message, setMessage] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [includeProject, setIncludeProject] = useState(Boolean(selectedProject?.sheet_id));
  const [projectAttachment, setProjectAttachment] = useState<ProjectAttachment>("google_sheets");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [result, setResult] = useState<FeedbackResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const selectedAttachment = event.target.files?.[0] ?? null;
    if (!selectedAttachment) {
      setAttachment(null);
      return;
    }
    const extension = selectedAttachment.name.split(".").pop()?.toLowerCase() ?? "";
    if (!FEEDBACK_ATTACHMENT_EXTENSIONS.has(extension)) {
      event.target.value = "";
      setAttachment(null);
      setResult({ kind: "error", message: t`This attachment type is not supported.` });
      return;
    }
    setAttachment(selectedAttachment);
    setResult(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setResult({ kind: "error", message: t`Please enter feedback before submitting.` });
      return;
    }

    setIsSubmitting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("message", trimmedMessage);
      formData.append("tags", JSON.stringify(tags));
      formData.append("projectAttachment", includeProject ? projectAttachment : "none");
      if (includeProject && selectedProject) {
        formData.append("projectId", selectedProject.id);
      }
      if (attachment) {
        formData.append("attachment", attachment);
      }

      const response = await authFetch(`${getApiUrl()}/feedback`, {
        method: "POST",
        body: formData,
      });

      const data: ErrorResponse = await response.json().catch((): ErrorResponse => ({}));

      if (!response.ok) {
        throw new Error(data.message || t`Feedback submission failed.`);
      }

      setMessage("");
      setTags([]);
      setAttachment(null);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      const warning = data.warnings?.[0];
      setResult({
        kind: "success",
        message: warning ? `${t`Thanks. Your feedback was submitted.`} ${warning}` : t`Thanks. Your feedback was submitted.`,
      });
    } catch (error) {
      setResult({
        kind: "error",
        message: error instanceof Error ? error.message : t`Feedback submission failed.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="feedback-page">
      <NavBar />
      <main className="feedback-shell">
        <div className="feedback-content">
          <button
            type="button"
            className="editor-return-button"
            onClick={() => navigate('/app')}
          >
            <ArrowBackIcon style={{ fontSize: '18px' }} />
            {t`Back to editor`}
          </button>
          <form className="feedback-card" onSubmit={handleSubmit}>
            <div className="feedback-header">
              <h1>{t`Send feedback`}</h1>
              <p>{t`Share an issue, an idea, or anything that could make FinTitles better.`}</p>
            </div>

            <label className="feedback-field">
              <span>{t`Feedback`}</span>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t`Tell us what happened or what would make this better.`}
                rows={7}
                maxLength={5000}
              />
            </label>

            <div className="feedback-field">
              <span>{t`Tags`}</span>
              <Autocomplete
                multiple
                options={[...FEEDBACK_TAGS]}
                value={tags}
                onChange={(_event, values): void => setTags(values)}
                renderTags={(values, getTagProps) => values.map((tag, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      label={tag}
                      className={`feedback-tag feedback-tag-${getTagTone(tag)}`}
                      size="small"
                    />
                  );
                })}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t`Choose one or more tags`} size="small" />
                )}
              />
            </div>

            <fieldset className="feedback-options">
              <label className="feedback-check-row">
                <input
                  type="checkbox"
                  checked={includeProject}
                  disabled={!selectedProject?.sheet_id}
                  onChange={(event) => setIncludeProject(event.target.checked)}
                />
                <span>
                  <strong>{t`Include project data`}</strong>
                  <small>{t`Recommended for project-specific issues—it helps us investigate faster.`}</small>
                </span>
              </label>

              {includeProject && selectedProject?.sheet_id && (
                <div className="feedback-radio-group" role="radiogroup" aria-label={t`Project data format`}>
                  <label>
                    <input
                      type="radio"
                      name="projectAttachment"
                      value="google_sheets"
                      checked={projectAttachment === "google_sheets"}
                      onChange={() => setProjectAttachment("google_sheets")}
                    />
                    {t`Google Sheets link`}
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="projectAttachment"
                      value="excel"
                      checked={projectAttachment === "excel"}
                      onChange={() => setProjectAttachment("excel")}
                    />
                    {t`Excel snapshot`}
                  </label>
                </div>
              )}

              {!selectedProject?.sheet_id && (
                <p className="feedback-option-note">{t`No linked project spreadsheet is available.`}</p>
              )}
            </fieldset>

            <label className="feedback-field">
              <span>{t`Attachment (optional)`}</span>
              <input
                className="feedback-file-input"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.pdf,.html,.htm,.tif,.tiff,.zip,.mp4,.mov,.m4v,.webm"
                aria-label={t`Attachment (optional)`}
                ref={attachmentInputRef}
                onChange={handleAttachmentChange}
              />
              <small>{t`Images, PDF, HTML, TIFF or TIFF ZIP, and video (MP4, MOV, M4V, WebM).`}</small>
            </label>

            <button className="feedback-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? t`Submitting...` : t`Submit`}
            </button>

            {result && (
              <p className={`feedback-message ${result.kind}`} role="status">
                {result.message}
              </p>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
